import { $, $$ } from '@utils';
import { Calendar } from 'vanilla-calendar-pro';
import i18next from 'i18next';

let calendarInstance = null;

function init() {
  const calendarEventsEl = $('[data-au-calendar-events]');
  if (!calendarEventsEl) return;

  if (calendarInstance) {
    try {
      calendarInstance.destroy();
    } catch {
      // instance may be detached after story switch
    }
    calendarInstance = null;
  }

  addEventListeners(calendarEventsEl);
  calendarDatalayer();
  applyColors();
}

function addEventListeners(calendarEventsEl) {
  const dateDivs = $$('[data-au-calendar-date]');
  const highlightedClass = 'au-datepicker__date-btn--highlighted';
  const noEventsContainer = $('[data-au-calendar-no-events]');
  const showMoreContainer = $('[data-au-calendar-cards-more]');
  const showMoreLink = $('[data-au-calendar-cards-more-link]');
  const seeMoreBtn = $$('[data-au-calendar-more]');
  const textContent = $$('[data-au-calendar-card-text]');
  const currentDate = new Date();
  let hiddenMonthEvents = [];
  let hiddenMonthEventDays = new Set();
  let pendingRevealDateStr = '';

  calendarEventsEl.addEventListener('click', (e) => {
    const clickedCell = e.target.closest('[data-vc-date]');
    if (!clickedCell) return;

    pendingRevealDateStr = clickedCell.dataset.vcDate || '';
  });

  // Array with all events
  const dates = [];
  const eventItems = [];

  function parseDateString(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  function formatDateToYmd(date) {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  dateDivs.forEach((div) => {
    const dateStr = div.getAttribute('data-au-calendar-date');
    const date = parseDateString(dateStr);
    dates.push(date);
    eventItems.push({ date, dateStr, element: div });
  });

  // Returns a deduplicated array of dates (one entry per unique day)
  function getUniqueDates(dateArray) {
    const seen = new Set();
    return dateArray.filter((date) => {
      const key = formatDateToYmd(date);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function getHighlightedDates() {
    return getUniqueDates(dates).reduce((acc, date) => {
      acc[formatDateToYmd(date)] = { modifier: highlightedClass };
      return acc;
    }, {});
  }

  function syncCurrentDate(selectedMonth, selectedYear) {
    currentDate.setFullYear(selectedYear, selectedMonth, 1);
  }

  // Function to get future events in chronological order from the current date
  function getNextFutureEvents(num = 3, excludeEvents = []) {
    const futureEvents = eventItems.filter((eventItem) => {
      return (
        eventItem.date >= currentDate &&
        !excludeEvents.some((excludeEvent) => excludeEvent.element === eventItem.element)
      );
    });

    return futureEvents.sort((a, b) => a.date - b.date).slice(0, num);
  }

  function revealHiddenMonthEvents() {
    hiddenMonthEvents.forEach((eventItem) => {
      eventItem.element.classList.remove('au-hide');
    });
    showMoreContainer.classList.add('au-hide');
    applyColors();
  }

  function shouldRevealForSelectedDate(dateStr) {
    if (!dateStr) return false;
    return hiddenMonthEventDays.has(dateStr);
  }

  // Function to update the calendar
  function updateCalendar() {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();

    // Filter events of the current month and year
    const filteredEvents = eventItems
      .filter((eventItem) => {
        return eventItem.date.getMonth() === month && eventItem.date.getFullYear() === year;
      })
      .sort((a, b) => a.date - b.date);

    const filteredUniqueDates = getUniqueDates(filteredEvents.map((eventItem) => eventItem.date));

    // Hide all event cards
    dateDivs.forEach((div) => div.classList.add('au-hide'));

    // If the month has its own events, display up to 3 event cards of the month
    let visibleEvents = [];
    if (filteredEvents.length > 0) {
      visibleEvents = filteredEvents.slice(0, 3);
    }

    // If the month has fewer than 3 cards, fill them with future cards
    if (visibleEvents.length < 3) {
      const remainingEvents = getNextFutureEvents(3 - visibleEvents.length, visibleEvents);
      visibleEvents = visibleEvents.concat(remainingEvents);
    }

    // Show the visible cards
    visibleEvents.forEach((eventItem) => {
      eventItem.element.classList.remove('au-hide');
    });

    hiddenMonthEvents = filteredEvents.slice(3);
    hiddenMonthEventDays = new Set(hiddenMonthEvents.map((eventItem) => eventItem.dateStr));
    // If the month has more than 3 cards, show the "See more" button
    if (hiddenMonthEvents.length > 0) {
      showMoreContainer.classList.remove('au-hide');
      showMoreLink.onclick = (e) => {
        e.preventDefault();
        revealHiddenMonthEvents();
      };
    } else {
      showMoreContainer.classList.add('au-hide');
      showMoreLink.onclick = null;
    }

    if (hiddenMonthEvents.length > 0 && shouldRevealForSelectedDate(pendingRevealDateStr)) {
      revealHiddenMonthEvents();
    }
    pendingRevealDateStr = '';

    // If there are no dates in this month, display a message
    const noEventsTextMonths = noEventsContainer.dataset.auCalendarNoEventsMonths;
    const noEventsTextFiltered = noEventsContainer.dataset.auCalendarNoEventsFilteredDates;

    function clearNoEventsContainer() {
      while (noEventsContainer.firstChild) {
        noEventsContainer.removeChild(noEventsContainer.firstChild);
      }
    }

    if (visibleEvents.length === 0) {
      noEventsContainer.classList.remove('au-hide');
      clearNoEventsContainer();

      const text = document.createElement('p');
      text.className = 'au-calendar__no-cards-text t-label';
      text.textContent = noEventsTextMonths;

      const image = document.createElement('img');
      image.className = 'au-calendar__no-cards-img';
      image.src = 'images/modules/Calendar/no-events.webp';
      image.alt = '';

      noEventsContainer.appendChild(text);
      noEventsContainer.appendChild(image);
    } else if (filteredUniqueDates.length === 0) {
      visibleEvents = getNextFutureEvents(3, []);
      noEventsContainer.classList.remove('au-hide');
      clearNoEventsContainer();

      const text = document.createElement('p');
      text.className = 't-label';
      text.textContent = noEventsTextFiltered;

      noEventsContainer.appendChild(text);
    } else {
      noEventsContainer.classList.add('au-hide');
      clearNoEventsContainer();
    }
    applyColors();
  }

  const options = {
    popups: getHighlightedDates(),
    selectedHolidays: dates,
    locale: i18next.language,
    styles: {
      calendar: 'au-datepicker',
      controls: 'au-datepicker__controls',
      grid: 'au-datepicker__grid',
      column: 'au-datepicker__column',
      header: 'au-datepicker__header',
      headerContent: 'au-datepicker__header-content',
      month: 'au-datepicker__month-selector',
      year: 'au-datepicker__year-selector',
      arrowPrev: 'au-datepicker__arrow au-datepicker__arrow--prev',
      arrowNext: 'au-datepicker__arrow au-datepicker__arrow--next',
      wrapper: 'au-datepicker__wrapper',
      content: 'au-datepicker__content',
      months: 'au-datepicker__months',
      monthsMonth: 'au-datepicker__month',
      years: 'au-datepicker__years',
      yearsYear: 'au-datepicker__year',
      week: 'au-datepicker__week',
      weekDay: 'au-datepicker__week-day',
      weekNumbers: 'au-datepicker__week-numbers',
      weekNumbersTitle: 'au-datepicker__week-numbers-title',
      weekNumbersContent: 'au-datepicker__week-numbers-content',
      weekNumber: 'au-datepicker__week-number',
      dates: 'au-datepicker__dates',
      date: 'au-datepicker__date',
      dateBtn: 'au-datepicker__date-btn',
      datePopup: 'au-datepicker__date-popup',
      dateRangeTooltip: 'au-datepicker__date-range-tooltip',
      time: 'au-datepicker__time',
      timeContent: 'au-datepicker__time-content',
      timeHour: 'au-datepicker__time-hour',
      timeMinute: 'au-datepicker__time-minute',
      timeKeeping: 'au-datepicker__time-keeping',
      timeRanges: 'au-datepicker__time-ranges',
      timeRange: 'au-datepicker__time-range',
    },
    onClickArrow(self, e) {
      const direction = e.target.dataset.vcArrow === 'next' ? 'siguiente' : 'previa';

      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickSlider',
        item_id: `fecha_${direction}`,
      });

      syncCurrentDate(self.context.selectedMonth, self.context.selectedYear);
      updateCalendar();
      resetViewMore();
    },
    onClickDate(self) {
      setTimeout(() => {
        syncCurrentDate(self.context.selectedMonth, self.context.selectedYear);
        updateCalendar();
        resetViewMore();
      }, 0);
    },
    onClickMonth(self) {
      syncCurrentDate(self.context.selectedMonth, self.context.selectedYear);
      updateCalendar();
    },
    onClickYear(self) {
      syncCurrentDate(self.context.selectedMonth, self.context.selectedYear);
      updateCalendar();
    },
    onUpdate(self) {
      syncCurrentDate(self.context.selectedMonth, self.context.selectedYear);
      updateCalendar();
    },
  };

  // initialization - pass element directly to bypass vanilla-calendar-pro's memoizedElements cache
  calendarInstance = new Calendar(calendarEventsEl, options);
  calendarInstance.init();

  // Function to select the date in the calendar when clicking "See more"
  function goToDateViewMore() {
    seeMoreBtn.forEach((btn) => {
      btn.addEventListener('click', () => {
        const date = new Date(btn.closest('[data-au-calendar-card]').getAttribute('data-au-calendar-date'));
        const month = date.getMonth();
        if (btn.textContent === 'Leer más') {
          calendarInstance.set({
            enableJumpToSelectedDate: true,
            selectedDates: [],
            selectedMonth: month,
          });
        } else {
          calendarInstance.set({
            enableJumpToSelectedDate: true,
            selectedDates: [date],
          });
          calendarInstance.update();
        }
      });
    });
  }
  // Function to hide the "See more" button for events when changing the month
  function resetViewMore() {
    textContent.forEach((text) => {
      if (text.classList.contains('show-more')) {
        text.classList.remove('show-more');
      }
    });

    seeMoreBtn.forEach((btn) => {
      if (btn.textContent === 'Leer menos') {
        btn.textContent = 'Leer más';
      }
    });
  }

  updateCalendar();
  goToDateViewMore();
}

// Function to send datalayer when click on month and year selectors
function calendarDatalayer() {
  document.addEventListener('click', (e) => {
    if (e.target.dataset.vc === 'month') {
      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickSelector',
        item_id: 'selector_month',
      });
    }

    if (e.target.dataset.vc === 'year') {
      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickSelector',
        item_id: 'selector_year',
      });
    }
  });
}

// Function to assign colors cyclically to the events
function applyColors() {
  const colors = ['#f3bdff', '#90ffbb', '#e0ff48'];
  const events = $$('[data-au-calendar-card]:not(.au-hide)');
  events.forEach((div, index) => {
    const colorIndex = index % colors.length;
    div.querySelector('.au-calendar-card__date').style.backgroundColor = colors[colorIndex];
  });
}

export default { init };
